import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Card,
  CardContent,
  Button,
  Stack,
  Divider,
  Chip,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import AdminIcon from '@mui/icons-material/AdminPanelSettings';
import PeopleIcon from '@mui/icons-material/People';
import SupportIcon from '@mui/icons-material/SupportAgent';
import AnalyticsIcon from '@mui/icons-material/Assessment';
import RoleIcon from '@mui/icons-material/Badge';
import AuditIcon from '@mui/icons-material/Security';

const AdminPortal: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);

  const employees = [
    { id: '1', name: 'Alice Support', role: 'Support Agent', status: 'Active', actions: 142 },
    { id: '2', name: 'Bob Manager', role: 'Regional Manager', status: 'Away', actions: 89 },
    { id: '3', name: 'Super Admin', role: 'Super Admin', status: 'Active', actions: 567 },
  ];

  const recentActions = [
    { user: 'Alice Support', action: 'Resolved Ticket #1024', time: '10 mins ago' },
    { user: 'Bob Manager', action: 'Approved Shop "Kiambu Greens"', time: '1 hour ago' },
    { user: 'Super Admin', action: 'Updated Global Commission Rate', time: '2 hours ago' },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={4}>
        <Box>
          <Typography variant="h4" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            Admin Control Center <AdminIcon color="primary" />
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage the Farmers HUB empire and your global workforce.
          </Typography>
        </Box>
        <Chip label="Super Admin Access" color="error" sx={{ fontWeight: 'bold' }} />
      </Stack>

      <Tabs 
        value={activeTab} 
        onChange={(_, val) => setActiveTab(val)} 
        sx={{ mb: 4, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab icon={<PeopleIcon />} label="User Management" />
        <Tab icon={<SupportIcon />} label="Employee Roles" />
        <Tab icon={<AnalyticsIcon />} label="Platform Health" />
        <Tab icon={<AuditIcon />} label="Audit Logs" />
      </Tabs>

      {activeTab === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Paper sx={{ p: 3, borderRadius: '16px' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>Platform Users</Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>User</TableCell>
                      <TableCell>Modules</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[
                      { name: 'John Farmer', modules: ['Farms', 'Seller'], status: 'Verified' },
                      { name: 'Mary Buyer', modules: ['Buyer'], status: 'Active' },
                      { name: 'Elite Logistics', modules: ['Logistics'], status: 'Verified' },
                    ].map((user, idx) => (
                      <TableRow key={idx}>
                        <TableCell>
                          <Stack direction="row" spacing={2} alignItems="center">
                            <Avatar sx={{ width: 32, height: 32 }}>{user.name[0]}</Avatar>
                            <Typography variant="body2" fontWeight="bold">{user.name}</Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          {user.modules.map(m => <Chip key={m} label={m} size="small" sx={{ mr: 0.5 }} />)}
                        </TableCell>
                        <TableCell>
                          <Chip label={user.status} size="small" color="success" variant="outlined" />
                        </TableCell>
                        <TableCell align="right">
                          <Button size="small">Manage</Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        </Grid>
      )}

      {activeTab === 1 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 3, borderRadius: '16px' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h6" fontWeight="bold">Employee Directory</Typography>
                <Button variant="contained" startIcon={<RoleIcon />}>Hire Employee</Button>
              </Stack>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Employee</TableCell>
                      <TableCell>Role</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Activity</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {employees.map((emp) => (
                      <TableRow key={emp.id}>
                        <TableCell>
                          <Stack direction="row" spacing={2} alignItems="center">
                            <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>{emp.name[0]}</Avatar>
                            <Typography variant="body2" fontWeight="bold">{emp.name}</Typography>
                          </Stack>
                        </TableCell>
                        <TableCell><Chip label={emp.role} size="small" /></TableCell>
                        <TableCell>
                          <Chip 
                            label={emp.status} 
                            size="small" 
                            color={emp.status === 'Active' ? 'success' : 'warning'} 
                          />
                        </TableCell>
                        <TableCell align="right">{emp.actions} actions</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3, borderRadius: '16px', bgcolor: 'grey.50' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>Real-Time Audit Feed</Typography>
              <List>
                {recentActions.map((action, idx) => (
                  <ListItem key={idx} disableGutters>
                    <ListItemText
                      primary={<Typography variant="body2" fontWeight="bold">{action.action}</Typography>}
                      secondary={<Typography variant="caption">{action.user} • {action.time}</Typography>}
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default AdminPortal;
